import { ObjectUtil, ValueDisplayValue } from "@mcleod/core";
import { ComponentPropDefinitionUtil, ComponentPropDefinitions, ComponentProps } from "../../base/ComponentProps";
import { PropType } from "../../base/PropType";
import { RadioBoxOption } from "./RadioBoxOption";

export interface RadioGroupProps extends ComponentProps {
    caption: string;
    captionVisible: boolean;
    captionVisibleInsideTable: boolean;
    defaultDataValue: boolean;
    items: ValueDisplayValue[] | (() => ValueDisplayValue[]) | RadioBoxOption[] | (() => RadioBoxOption[]);
    printable: boolean;
    printableDuringAdd: boolean;
    printableDuringSearch: boolean;
    printableDuringUpdate: boolean;
}

export abstract class RadioGroupPropDefinitions {
    private static radioGroupPropsDefs: ComponentPropDefinitions;

    public static getDefinitions(): ComponentPropDefinitions {
        if (this.radioGroupPropsDefs == null) {
            this.radioGroupPropsDefs = {
                ...ComponentPropDefinitionUtil.getComponentPropertyDefinitions(),
                ...ComponentPropDefinitionUtil.getDataBoundPropertyDefinitions(),
                ...this.buildSimpleProps(),
                ...this.buildPrintableProps(),
                ...this.buildEventProps()
            };
            ComponentPropDefinitionUtil.populateComponentPropNames(this.radioGroupPropsDefs);
        }
        return this.radioGroupPropsDefs;
    }

    private static buildSimpleProps(): ComponentPropDefinitions {
        return {
            caption: {
                type: PropType.string,
                visibleInMcLeodTailor: true, 
                description: "This specifies the text that will appear at the top of the radio group."
            },
            captionVisible: {
                type: PropType.bool,
                defaultValue: true,
                visibleInMcLeodTailor: true,
                description: "This toggles whether or not the caption of the radio group will be shown.  If set to false, there will be no caption or additional whitespace where the caption would normally be displayed."
            },
            captionVisibleInsideTable: {
                type: PropType.bool,
                defaultValue: false,
                visibleInMcLeodTailor: true,
                description: "This specifies if the caption should be displayed when the radio group is presented inside of a table row. "
            },
            defaultDataValue: {
                type: PropType.string,
                visibleInMcLeodTailor: true, 
                category: "Data",
                source: "databound",
                description: "This specifies the default (selected) value when entering add mode."
            },
            focusable: {
                type: PropType.bool,
                defaultValue: true, 
                visibleInMcLeodTailor: true,
                description: "This toggles whether or not this radio group can receive focus."
            },
            items: {
                // This is a placeholder, so that 'items' is a valid property for every type of radio group.
                // The items property should be updated in every subclass' props class to specify the type and
                // description values (at least).
                type: PropType.radioButtonGroupItem, // Had to pick one
                visibleInMcLeodTailor: true,
                description: "This specifies the list of items that will be represented as radio options."
            }
        };
    }

    private static buildPrintableProps(): ComponentPropDefinitions {
        return {
            printable: {
                type: PropType.bool,
                defaultValue: false,
                category: "Appearance",
                visibleInMcLeodTailor: true,
                description: "This toggles whether the items in the radio group appear as editable (printable = false) or as a caption that indicates the current selection (printable = true).  This is often changed at runtime.",
            },
            printableDuringAdd: {
                type: PropType.bool,
                defaultValue: false,
                category: "Appearance",
                visibleInMcLeodTailor: true,
                description: "This toggles whether the items in the radio group appear as printable during add mode.",
            },
            printableDuringSearch: {
                type: PropType.bool,
                defaultValue: false,
                category: "Appearance",
                visibleInMcLeodTailor: true,
                description: "This toggles whether the items in the radio group appear as printable during search mode.",
            },
            printableDuringUpdate: {
                type: PropType.bool,
                defaultValue: false,
                category: "Appearance",
                visibleInMcLeodTailor: true,
                description: "This toggles whether the items in the radio group appear as printable during update mode.",
            }
        };
    }

    private static buildEventProps(): ComponentPropDefinitions {
        return {
            beforeSelection: {
                type: PropType.event,
                category: "Events",
                addListenerMethod: "addBeforeSelectionListener",
                removeListenerMethod: "removeBeforeSelectionListener",
                eventSignature: "BeforeSelection(event: RadioOptionSelectionEvent)",
                description: "This specifies a function to be called before the user's selection of a particular radio option takes effect.  It does allow the selection to be cancelled."
            },
            afterSelection: {
                type: PropType.event,
                category: "Events",
                addListenerMethod: "addAfterSelectionListener",
                removeListenerMethod: "removeAfterSelectionListener",
                eventSignature: "AfterSelection(event: RadioOptionSelectionEvent)",
                description: "This specifies a function to be called after the user selects a particular radio option in the group."
            },
            onPrintable: {
                type: PropType.event,
                category: "Events",
                addListenerMethod: "addPrintableListener",
                removeListenerMethod: "removePrintableListener",
                eventSignature: "OnPrintable(event: PrintableEvent)",
                description: "This specifies a function to be called when the radio group's printable property has changed."
            }
        };
    }

    public static cloneDefs(): ComponentPropDefinitions {
        return ObjectUtil.deepCopy(RadioGroupPropDefinitions.getDefinitions());
    }
}
